/**
 * Stage 27 · ONE Platform Studio.
 * Suntronix admins can create custom objects, fields, and promote extension
 * packages through Development → UAT → Production. Platform guardrails
 * (tenant isolation, golden-asset immutability, audit, legal holds, financial
 * posting, erasure certification) are enforced server-side and cannot be
 * weakened by any package.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Layers, Package, Plus, ArrowRight, Lock, ShieldCheck, GripVertical, Eye, Settings } from "lucide-react";

const FIELD_TYPES = ["text","number","date","boolean","currency","dropdown","multiselect","reference","file","calculated","email","url"];
const ENV_ORDER = ["development","uat","production"];

export default function Studio() {
  const [tab, setTab] = useState("objects");
  return (
    <div className="max-w-6xl space-y-5" data-testid="studio">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Administration · Stage 27</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">ONE Platform Studio</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Extend ONE without waiting for a developer. Build custom objects and fields, package them, and promote through Development → UAT → Production. Platform guardrails remain enforced.
        </p>
      </div>
      <div className="rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-2 text-xs inline-flex items-center gap-2">
        <ShieldCheck className="h-4 w-4" /> Studio cannot weaken tenant isolation, golden-asset immutability, audit, legal holds, financial posting or erasure certification.
      </div>
      <nav className="flex flex-wrap gap-1 border-b border-slate-200" role="tablist">
        {[
          { key: "objects",  label: "Custom objects",   icon: Layers },
          { key: "packages", label: "Extension packages", icon: Package },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`studio-tab-${key}`}
            className={"px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px " +
              (tab === key ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </nav>
      {tab === "objects"  && <ObjectsTab />}
      {tab === "packages" && <PackagesTab />}
    </div>
  );
}

function ObjectsTab() {
  const [items, setItems] = useState([]); const [showForm, setShowForm] = useState(false);
  const [reserved, setReserved] = useState([]);
  const [builderSlug, setBuilderSlug] = useState(null);
  const load = () => api.get("/studio/objects").then(({ data }) => { setItems(data.items || []); setReserved(data.reserved_names || []); });
  useEffect(() => { load().catch(() => {}); }, []);
  return (
    <div className="space-y-3" data-testid="studio-objects">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="studio-object-new">
          <Plus className="h-4 w-4" /> New object
        </button>
      </div>
      {showForm && <ObjectForm reserved={reserved} onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">Slug</th><th className="text-left py-2 px-3">Label</th><th className="text-right py-2 px-3">Fields</th><th className="text-left py-2 px-3">Env</th><th className="text-left py-2 px-3">Version</th><th className="text-right py-2 px-3">Form</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center text-slate-500">No custom objects yet.</td></tr>
            ) : items.map((o) => (
              <tr key={o.id} className="border-t border-slate-100" data-testid={`studio-obj-${o.slug}`}>
                <td className="py-2 px-3 font-mono text-slate-900">{o.slug}</td>
                <td className="py-2 px-3 text-slate-800">{o.label}</td>
                <td className="py-2 px-3 text-right tabular-nums">{(o.fields || []).length}</td>
                <td className="py-2 px-3"><span className={envChip(o.env)}>{o.env}</span></td>
                <td className="py-2 px-3 text-slate-600">v{o.version}</td>
                <td className="py-2 px-3 text-right">
                  <button onClick={() => setBuilderSlug(o.slug)}
                    disabled={o.env === "production"}
                    className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`studio-form-${o.slug}`}
                    title={o.env === "production" ? "Production objects are immutable — clone into a new version" : "Configure form"}>
                    <Settings className="h-3 w-3" /> Configure form
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {reserved.length > 0 && (
        <details className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600">
          <summary className="cursor-pointer font-medium text-slate-700">Reserved system names ({reserved.length})</summary>
          <div className="mt-2 flex flex-wrap gap-1">
            {reserved.map((r) => <span key={r} className="font-mono text-[10px] px-1.5 py-0.5 bg-white border border-slate-200 rounded">{r}</span>)}
          </div>
        </details>
      )}
      {builderSlug && (
        <FormBuilderModal slug={builderSlug} onClose={() => setBuilderSlug(null)} onSaved={() => { setBuilderSlug(null); load(); }} />
      )}
    </div>
  );
}

/* ============================================================ Form Builder */
function FormBuilderModal({ slug, onClose, onSaved }) {
  const [obj, setObj] = useState(null);
  const [fields, setFields] = useState([]);
  const [mode, setMode] = useState("create");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/studio/objects/${slug}`).then(({ data }) => {
      setObj(data);
      const src = (data.fields || []).slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      // ensure defaults
      setFields(src.map((f) => ({
        ...f,
        show_in_create: f.show_in_create ?? true,
        show_in_edit:   f.show_in_edit   ?? true,
        show_in_list:   f.show_in_list   ?? true,
      })));
    }).catch(() => toast.error("Failed to load object"));
  }, [slug]);
  const move = (i, delta) => {
    setFields((s) => {
      const arr = s.slice();
      const j = i + delta;
      if (j < 0 || j >= arr.length) return s;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
  };
  const toggle = (i, key) => setFields((s) => s.map((f, j) => j === i ? { ...f, [key]: !f[key] } : f));
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/studio/objects/${slug}/form-layout`, {
        field_order: fields.map((f) => f.slug),
        visibility: fields.reduce((acc, f) => (acc[f.slug] = {
          show_in_create: !!f.show_in_create,
          show_in_edit:   !!f.show_in_edit,
          show_in_list:   !!f.show_in_list,
        }, acc), {}),
      });
      toast.success("Form layout saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!obj) return null;
  const flagKey = mode === "create" ? "show_in_create" : mode === "edit" ? "show_in_edit" : "show_in_list";
  const visible = fields.filter((f) => f[flagKey]);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex" onClick={onClose} data-testid="studio-form-builder">
      <div className="ml-auto w-full max-w-5xl h-full bg-white shadow-xl overflow-y-auto flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <div>
            <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Form builder</div>
            <h2 className="text-xl font-heading font-semibold text-slate-900 tracking-tight">{obj.label} · <span className="font-mono text-slate-500 text-base">{obj.slug}</span></h2>
          </div>
          <div className="ml-auto flex items-center gap-1 border border-slate-200 rounded-md p-1 text-xs">
            {["create", "edit", "list"].map((m) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`studio-form-mode-${m}`}
                className={"px-2 py-1 rounded " + (mode === m ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100")}>
                {m}
              </button>
            ))}
          </div>
          <button onClick={onClose} className="h-9 px-3 text-sm rounded-md border border-slate-200">Close</button>
          <button onClick={save} disabled={saving} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white disabled:opacity-60" data-testid="studio-form-save">
            {saving ? "Saving…" : "Save layout"}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-0 flex-1 min-h-0">
          {/* Left: designer */}
          <div className="border-r border-slate-200 overflow-y-auto p-4 space-y-1">
            <div className="text-[10px] font-semibold tracking-widest uppercase text-slate-500 mb-2">Order fields (top → bottom) and choose visibility</div>
            {fields.map((f, i) => (
              <div key={f.slug} className="rounded-md border border-slate-200 bg-white p-2 flex items-center gap-2" data-testid={`studio-fb-${f.slug}`}>
                <GripVertical className="h-4 w-4 text-slate-400" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 truncate">{f.label} <span className="text-slate-400 font-mono text-xs">{f.slug}</span></div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{f.type}{f.required ? " · required" : ""}</div>
                </div>
                <div className="flex items-center gap-1 text-[10px]">
                  <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!f.show_in_create} onChange={() => toggle(i, "show_in_create")} data-testid={`studio-fb-${f.slug}-create`} /> create</label>
                  <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!f.show_in_edit}   onChange={() => toggle(i, "show_in_edit")} /> edit</label>
                  <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!f.show_in_list}   onChange={() => toggle(i, "show_in_list")} /> list</label>
                </div>
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => move(i, -1)} className="text-xs px-1 rounded border border-slate-200 hover:bg-slate-50" data-testid={`studio-fb-up-${f.slug}`}>↑</button>
                  <button onClick={() => move(i, +1)} className="text-xs px-1 rounded border border-slate-200 hover:bg-slate-50" data-testid={`studio-fb-down-${f.slug}`}>↓</button>
                </div>
              </div>
            ))}
          </div>
          {/* Right: preview */}
          <div className="overflow-y-auto p-4 bg-slate-50" data-testid="studio-form-preview">
            <div className="text-[10px] font-semibold tracking-widest uppercase text-slate-500 mb-2 flex items-center gap-1"><Eye className="h-3 w-3" /> Preview · {mode} form</div>
            <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
              {visible.length === 0 ? (
                <div className="text-sm text-slate-500">No fields on this form — toggle at least one field into <b>{mode}</b>.</div>
              ) : visible.map((f) => <PreviewField key={f.slug} f={f} />)}
              {mode !== "list" && (
                <div className="flex justify-end pt-2">
                  <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white opacity-60 cursor-not-allowed">Save {obj.label}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewField({ f }) {
  const label = (
    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
      {f.label} {f.required && <span className="text-rose-600">*</span>}
    </div>
  );
  if (f.type === "boolean") return <label className="flex items-center gap-2 text-sm"><input type="checkbox" disabled /> <span className="text-slate-800">{f.label}</span></label>;
  if (f.type === "dropdown") return <label className="block">{label}<select className="input w-full" disabled><option>{(f.options || [])[0] || "—"}</option></select></label>;
  if (f.type === "multiselect") return <label className="block">{label}<div className="flex flex-wrap gap-1">{(f.options || []).map((o) => <span key={o} className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700">{o}</span>)}</div></label>;
  if (f.type === "date")   return <label className="block">{label}<input type="date" className="input w-full" disabled /></label>;
  if (f.type === "number" || f.type === "currency") return <label className="block">{label}<input type="number" className="input w-full" disabled /></label>;
  if (f.type === "file")   return <label className="block">{label}<div className="input w-full text-slate-400 text-sm">Attach file…</div></label>;
  return <label className="block">{label}<input className="input w-full" disabled placeholder={f.help_text || ""} /></label>;
}

function ObjectForm({ reserved, onCancel, onCreated }) {
  const [f, setF] = useState({ slug: "", label: "", plural_label: "", description: "", fields: [{ slug: "name", label: "Name", type: "text", required: true }] });
  const setField = (i, upd) => setF((s) => ({ ...s, fields: s.fields.map((x, j) => j === i ? { ...x, ...upd } : x) }));
  const addField = () => setF((s) => ({ ...s, fields: [...s.fields, { slug: "", label: "", type: "text" }] }));
  const removeField = (i) => setF((s) => ({ ...s, fields: s.fields.filter((_, j) => j !== i) }));
  const save = async () => {
    if (reserved.includes(f.slug.toLowerCase())) { toast.error("That slug is reserved."); return; }
    try {
      await api.post("/studio/objects", { ...f, fields: f.fields.map((x) => ({ ...x, options: (x.options || "").toString().split(",").map((s) => s.trim()).filter(Boolean) })) });
      toast.success("Object created (development)"); onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="studio-object-form">
      <div className="grid grid-cols-2 gap-2">
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Slug</div>
          <input className="input" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} placeholder="sim_card, printer, av_equipment" data-testid="studio-object-slug" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Label</div>
          <input className="input" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="studio-object-label" /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Description</div>
          <input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></label>
      </div>
      <div className="border-t border-slate-100 pt-2">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Fields</div>
        {f.fields.map((fld, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center mb-1">
            <input className="input col-span-3" placeholder="slug" value={fld.slug} onChange={(e) => setField(i, { slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} data-testid={`studio-field-slug-${i}`} />
            <input className="input col-span-3" placeholder="label" value={fld.label} onChange={(e) => setField(i, { label: e.target.value })} />
            <select className="input col-span-2" value={fld.type} onChange={(e) => setField(i, { type: e.target.value })}>
              {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input className="input col-span-2" placeholder="options (csv)" value={fld.options || ""} onChange={(e) => setField(i, { options: e.target.value })} />
            <label className="col-span-1 text-xs flex items-center gap-1"><input type="checkbox" checked={fld.required} onChange={(e) => setField(i, { required: e.target.checked })} /> req</label>
            <button className="col-span-1 text-xs text-rose-600 border border-slate-200 rounded h-9" onClick={() => removeField(i)}>×</button>
          </div>
        ))}
        <button onClick={addField} className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"><Plus className="h-3 w-3" /> Add field</button>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="studio-object-save">Create object</button>
      </div>
    </div>
  );
}

function PackagesTab() {
  const [items, setItems] = useState([]); const [objects, setObjects] = useState([]); const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/studio/packages").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load().catch(() => {}); api.get("/studio/objects").then(({ data }) => setObjects(data.items || [])); }, []);
  const promote = async (id, target) => {
    if (!window.confirm(`Promote package to ${target.toUpperCase()}?`)) return;
    try { await api.post(`/studio/packages/${id}/promote`, { target_env: target }); toast.success(`Promoted to ${target}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="studio-packages">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="studio-pkg-new">
          <Plus className="h-4 w-4" /> New package
        </button>
      </div>
      {showForm && <PackageForm objects={objects} onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">Name</th><th className="text-right py-2 px-3">Objects</th><th className="text-left py-2 px-3">Env</th><th className="text-left py-2 px-3">Version</th><th className="text-right py-2 px-3">Promote</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="py-6 text-center text-slate-500">No packages yet.</td></tr>
            ) : items.map((p) => {
              const idx = ENV_ORDER.indexOf(p.env);
              const next = idx >= 0 && idx < ENV_ORDER.length - 1 ? ENV_ORDER[idx + 1] : null;
              return (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="py-2 px-3 text-slate-900">{p.name}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{(p.object_slugs || []).length}</td>
                  <td className="py-2 px-3"><span className={envChip(p.env)}>{p.env}</span></td>
                  <td className="py-2 px-3 text-slate-600">v{p.version}</td>
                  <td className="py-2 px-3 text-right">
                    {next ? (
                      <button onClick={() => promote(p.id, next)} className="text-xs px-2 py-0.5 rounded border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white inline-flex items-center gap-1" data-testid={`studio-promote-${p.id}`}>
                        Promote to {next} <ArrowRight className="h-3 w-3" />
                      </button>
                    ) : (
                      <span className="text-xs text-emerald-700 inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Production</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PackageForm({ objects, onCancel, onCreated }) {
  const [f, setF] = useState({ name: "", description: "", object_slugs: [] });
  const toggle = (slug) => setF((s) => ({ ...s, object_slugs: s.object_slugs.includes(slug) ? s.object_slugs.filter((x) => x !== slug) : [...s.object_slugs, slug] }));
  const save = async () => {
    try { await api.post("/studio/packages", f); toast.success("Package created"); onCreated(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="studio-pkg-form">
      <div className="grid grid-cols-2 gap-2">
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Name</div>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="studio-pkg-name" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Description</div>
          <input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></label>
      </div>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Objects</div>
        <div className="grid grid-cols-2 gap-1 max-h-[200px] overflow-y-auto">
          {objects.map((o) => (
            <label key={o.slug} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.object_slugs.includes(o.slug)} onChange={() => toggle(o.slug)} />
              <span className="text-slate-800">{o.label}</span>
              <span className="text-[10px] font-mono text-slate-500">{o.slug}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="studio-pkg-save">Create package</button>
      </div>
    </div>
  );
}

function envChip(e) {
  return "inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " +
    (e === "production" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
     e === "uat"        ? "bg-amber-100 text-amber-800 border-amber-200" :
                           "bg-slate-100 text-slate-700 border-slate-200");
}
